import React, { Fragment, useContext, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import 'react-slidedown/lib/slidedown.css'
import { XCircle } from '@styled-icons/boxicons-solid'
import HarnessesSearchTable from './HarnessesSearchTable'
import HarnessLevelsSearchTable from './HarnessLevelsSearchTable'
import {
  handleEnterKey,
  SELECT_ALL,
  DESELECT_ALL,
  downloadAsZip,
} from './utils'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import LoadingSpinnerOverlay from '../common/LoadingSpinnerOverlay'
import { NavContext } from '../../context/global_context'
import { deleteRequest, getRequest, postRequest } from '../../services/base'
import { downloadErrorLogs } from '../../utils/common'
import CheckBox from '../common/CheckBox'

const ConnectorViewForm = (props) => {
  const { vehicle, connector_view } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [value, dispatch] = useContext(NavContext)

  const [connectorView, setConnectorView] = useState({})
  const references = useRef([])
  const [state, setState] = useState({
    selectedHarnesses: [],
    openedHarnesses: [],
    connectorViewName: '',
    checkedHarnessLevels: [],
    loading: false,
    svg: '',
    png: '',
    searchHarnessTableComponents: false,
    svg_removed: false,
    png_removed: false,
    mapping_json: '',
    mapping_json_removed: false,
    mapping_logs: {},
  })
  const [autoMapped, setAutoMapped] = useState(false)

  const { persisted, clone_from } = props

  useEffect(() => {
    dispatch({
      type: 'header',
      data: {
        header: 'Admin Tools',
        sub_header: `Vehicle List / ${vehicle} / Vehicle Views / ${connector_view ? 'Edit Vehicle View' : 'New Vehicle View'}`,
        id: '',
        show_back_btn: true,
        dtc_details: {},
        vehicle: vehicle,
        back_btn_url: `/vehicles/${vehicle}/connector_views/`,
      },
    })

    if (connector_view) {
      getConnectorViewData(connector_view)
    }
    if (searchParams.get('clone_from') && clone_from) {
      getConnectorViewData(searchParams.get('clone_from'))
    }
  }, [])

  const getConnectorViewData = async (id) => {
    setState({ ...state, loading: true })

    let res = await getRequest(`/api/connector_views/${id}/`)
    setConnectorView(res)
    setState({
      ...state,
      loading: false,
      selectedHarnesses: res?.harness_ids,
      checkedHarnessLevels: res?.harness_levels_ids,
      connectorViewName: res?.name,
      svg: res?.svg_name,
      png: res?.png_name,
      mapping_json: res?.mapping_json_name,
      mapping_logs: res?.mapping_logs,
    })
    references.current = res?.group_ids
  }

  const hlCheckHandler = (harnessLevelIds, action) => {
    const selectedHarnessLevels = new Set(state.checkedHarnessLevels || [])

    harnessLevelIds.forEach((hl) => {
      if (
        (selectedHarnessLevels.has(hl) && action !== SELECT_ALL) ||
        action === DESELECT_ALL
      ) {
        selectedHarnessLevels.delete(hl)
      } else {
        selectedHarnessLevels.add(hl)
      }
    })

    setState({
      ...state,
      checkedHarnessLevels: Array.from(selectedHarnessLevels),
    })
  }

  const connectorViewNameChange = (event) => {
    setState({ ...state, connectorViewName: event.target.value })
  }

  const selectHarness = (harness) => {
    setState({
      ...state,
      selectedHarnesses: [...state.selectedHarnesses, harness],
      openedHarnesses: [...state.openedHarnesses, harness.id],
      searchHarnessTableComponents: false,
    })
  }

  const clearHarness = (harness, harnessLevels) => {
    const harnesses = state.selectedHarnesses
    const index = harnesses.indexOf(harness)
    if (index !== -1) {
      harnesses.splice(index, 1)
    }
    const harnessLevelsIds = harnessLevels.map((hl) => hl.id)
    const afterClear = state.checkedHarnessLevels.filter(
      (sl) => !harnessLevelsIds.includes(sl)
    )
    setState({
      ...state,
      selectedHarnesses: harnesses,
      checkedHarnessLevels: afterClear,
    })
  }

  const renderHarnessSearchTable = () => {
    setState({
      ...state,
      searchHarnessTableComponents: true,
      openedHarnesses: [],
    })
  }

  const hideHarnessSearchTable = () => {
    setState({ ...state, searchHarnessTableComponents: false })
  }

  const toggleHarnesses = (harness) => {
    let openedHarnesses = [...state.openedHarnesses]
    if (openedHarnesses.includes(harness.id)) {
      setState({
        ...state,
        openedHarnesses: openedHarnesses.filter((h) => h !== harness.id),
      })
    } else {
      setState({ ...state, openedHarnesses: [...openedHarnesses, harness.id] })
    }
  }

  const harnessIsOpened = (h) => {
    return state.openedHarnesses.includes(h.id)
  }

  const fileChanged = (e, type) => {
    const input = e.target

    if (input.value) {
      if (type === 'svg') {
        changeFile(input.files[0])
      }
      setState({
        ...state,
        [`${type}`]: input.files[0].name,
        [`${type}_removed`]: false,
      })
    }
  }

  const clearFile = (type) => {
    if (window.confirm(`Are you really wan't to remove ${type}`) == true) {
      setState({ ...state, [`${type}`]: null, [`${type}_removed`]: true })
      references.current = []
      if (connectorView?.id) {
        deleteImages(type)
      }
    }
  }

  const deleteImages = async (type) => {
    let res = await deleteRequest(
      `/api/connector_views/delete_views/${connectorView?.id}/${type}/`
    )

    dispatch({
      type: 'notification',
      data: {
        notification_type: 'success',
        msg: `${type} Successfully Delete`,
      },
    })

    getConnectorViewData(connectorView?.id)
  }

  const readFile = (event) => {
    const parser = new DOMParser()
    const svgDoc = parser.parseFromString(event.target.result, 'image/svg+xml')

    let referenceIds = []
    svgDoc.childNodes.forEach((main, index) => {
      main.childNodes.forEach((parent, parentIndex) => {
        parent.childNodes.forEach((child, childIndex) => {
          if (child.id) {
            referenceIds.push(child.id)
          }
        })
      })
    })
    references.current = referenceIds
  }

  const changeFile = () => {
    let element = document.getElementById('svg_attachment')
    var file = element.files[0]
    var reader = new FileReader()
    reader.addEventListener('load', readFile)
    reader.readAsText(file)
  }

  const onSubmit = async (e) => {
    e.preventDefault()

    setState({ ...state, loading: true })

    const form = e.target
    const formData = new FormData(form)

    formData.append('png_removed', state.png_removed)
    formData.append('svg_removed', state.svg_removed)
    formData.append('mapping_json_removed', state.mapping_json_removed)

    references.current.forEach((reference) => {
      formData.append('references', reference)
    })

    if (searchParams.get('clone_from') && clone_from) {
      formData.append('clone_from_id', searchParams.get('clone_from'))
      formData.append('png_name', connectorView?.png_name)
      formData.append('svg_name', connectorView?.svg_name)
    }

    let res = await postRequest(
      `/api/connector_views/save_connector_view/`,
      formData
    )
    setState({ ...state, loading: false })

    if (res?.non_field_errors) {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: `Error :- ${res?.non_field_errors.toString()}`,
        },
      })
    } else {
      if (connectorView?.id && !clone_from) {
        document.getElementById('svg_attachment').value = null
        document.getElementById('png_attachment').value = null
        getConnectorViewData(connectorView?.id)
      } else {
        navigate(
          `/vehicles/${vehicle}/connector_views/${res?.id}/edit_mappings`
        )
      }
    }
  }

  return (
    <>
      <div className="flex items-stretch flex-grow text-black">
        <LoadingSpinnerOverlay active={state.loading} />
        <div className="flex-1 w-1/2 flex flex-col bg-tracer-darker-blue pt-6 flex-grow">
          {state?.checkedHarnessLevels?.length > 0 ||
          state?.selectedHarnesses?.length > 0 ? (
            state.selectedHarnesses.map((selectedHarness) => (
              <HarnessLevelsSearchTable
                key={`${selectedHarness.id}-harness-level`}
                harness={selectedHarness}
                checkedHarnessLevels={state.checkedHarnessLevels}
                onSelectHarnessLevel={(id, action) =>
                  hlCheckHandler(id, action)
                }
                onClearHarness={clearHarness}
                toggleHarnesses={toggleHarnesses}
                harnessIsOpened={harnessIsOpened}
                vehicle={vehicle}
              />
            ))
          ) : (
            <HarnessesSearchTable
              selectedHarnessesIds={state.selectedHarnesses.map((sh) => sh.id)}
              onSelectHarness={selectHarness}
              vehicle={vehicle}
            />
          )}

          {state.searchHarnessTableComponents && (
            <div className="flex flex-col">
              <HarnessesSearchTable
                selectedHarnessesIds={state.selectedHarnesses.map(
                  (sh) => sh.id
                )}
                onSelectHarness={selectHarness}
                vehicle={vehicle}
              />
              <button
                onClick={hideHarnessSearchTable}
                className="btn my-8 mx-4"
              >
                Hide Harness Search Table
              </button>
            </div>
          )}
          {state.searchHarnessTableComponents === false &&
            state.selectedHarnesses.length > 0 && (
              <button
                onClick={renderHarnessSearchTable}
                className="btn my-8 mx-4"
              >
                Add Harness
              </button>
            )}
        </div>

        <form
          className="flex-1 w-1/2 flex flex-col p-4 pt-6 bg-tracer-dark-blue text-white flex-grow"
          action={''}
          acceptCharset="UTF-8"
          method="post"
          onSubmit={onSubmit}
          encType="multipart/form-data"
        >
          <input type="hidden" name="clone_from" value={clone_from} />
          <input type="hidden" name="vehicle_key" value={vehicle} />
          {persisted && connectorView?.id && (
            <input name="id" type="hidden" value={connectorView?.id} />
          )}
          {state.selectedHarnesses.map((selectedHarness, i) => (
            <Fragment key={`select-hrn-${selectedHarness.id}`}>
              {i === 0 && (
                <input
                  type="hidden"
                  name="harness"
                  value={selectedHarness.id}
                />
              )}
              <input
                type="hidden"
                name="harness_ids"
                value={selectedHarness.id}
              />
            </Fragment>
          ))}
          {state.checkedHarnessLevels.map((hl, i) => (
            <input
              key={`checked-harness-${i}`}
              type="hidden"
              name="harness_levels_ids"
              value={hl}
            />
          ))}
          <div className="flex">Vehicle View Name</div>
          <div className="flex mt-6">
            <input
              autoComplete="off"
              name={'name'}
              className="w-full bg-white  text-black h-10 px-5 pr-5 rounded focus:outline-none"
              placeholder="Front View"
              onChange={connectorViewNameChange}
              value={state.connectorViewName || ''}
              onKeyDown={handleEnterKey}
              required={true}
            />
          </div>

          {persisted && (
            <div>
              <div className="flex mt-6">Current Image</div>

              <img src={connectorView.compressed_png} />
            </div>
          )}

          <div className="flex mt-6">Images</div>

          {persisted && (
            <div>
              <a
                className={
                  'inline-block text-blue-500 underline cursor-pointer'
                }
                onClick={() => {
                  downloadAsZip(
                    connectorView?.id,
                    connectorView?.name,
                    dispatch
                  )
                }}
              >
                Download persisted as ZIP
              </a>
            </div>
          )}

          <div className="flex flex-col w-full mt-6">
            <label htmlFor="svg_attachment">SVG</label>
            <input
              name="svg"
              type="file"
              id="svg_attachment"
              onChange={(e) => fileChanged(e, 'svg')}
              style={{ display: state.svg ? 'none' : 'inline' }}
              accept={'image/svg'}
            />
            {state.svg && (
              <div>
                {!clone_from && (
                  <XCircle
                    className={'w-4 text-red-500 mr-3'}
                    onClick={(e) => clearFile('svg')}
                  />
                )}
                {state.svg}
              </div>
            )}
            {persisted && (
              <div className="text-red-600 text-sm">
                Updating the SVG image will erase all existing mappings!
              </div>
            )}
            <label htmlFor="png_attachment">PNG</label>
            <input
              name="png"
              type="file"
              id="png_attachment"
              onChange={(e) => fileChanged(e, 'png')}
              style={{ display: state.png ? 'none' : 'inline' }}
              accept={'image/svg'}
            />
            {state.png && (
              <div>
                {!clone_from && (
                  <XCircle
                    className={'w-4 text-red-500 mr-3'}
                    onClick={(e) => clearFile('png')}
                  />
                )}
                {state.png}
              </div>
            )}
            {/* <label className="py-2" htmlFor="mapping_json">
            Mapping Json
          </label>
          <input
            name="mapping_json"
            type="file"
            id="mapping_json"
            onChange={(e) => fileChanged(e, 'mapping_json')}
            style={{ display: state.mapping_json ? 'none' : 'inline' }}
            accept={'application/JSON'}
          /> */}
            {/* {state.mapping_json && (
            <div>
              {!clone_from && (
                <XCircle
                  className={'w-4 text-red-500 mr-3'}
                  onClick={(e) => clearFile('mapping_json')}
                />
              )}
              {state.mapping_json}
            </div>
          )} */}
          </div>

          {/* {state?.mapping_logs?.error?.length > 0 && (
          <div>
            <a
              className={'inline-block text-blue-500 underline cursor-pointer'}
              onClick={() => {
                downloadErrorLogs(
                  state?.mapping_logs?.error,
                  'connector_view_mapping_logs.txt'
                )
              }}
            >
              Download mapping logs
            </a>
          </div>
        )} */}

          {/* <span className="flex items-center py-2">
          <div className="mr-3">
            <CheckBox
              name="auto_mapping"
              value={autoMapped}
              checked={autoMapped}
              onChange={(e) => {
                setAutoMapped(e.target.checked)
              }}
            />
          </div>
          <span className="px-2 ">Auto Mapped Connector</span>
        </span> */}
          <div className="flex justify-center mt-20">
            <input
              type="button"
              className={`cursor-pointer btn danger my-4 px-5 mr-4`}
              style={{ width: 100 }}
              onClick={(e) => navigate(`/vehicles/${vehicle}/connector_views`)}
              value="Cancel"
            />
            <button type="submit" className="btn my-4">
              {persisted ? 'Save Vehicle View Changes' : 'Add Vehicle View'}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

ConnectorViewForm.propTypes = {
  harnesses: PropTypes.array,
  selectedLevelsIds: PropTypes.array,
  persisted: PropTypes.bool,
  connectorView: PropTypes.object,
  clone_from: PropTypes.bool,
}

export default ConnectorViewForm
